import classNames from 'classnames';
import { IFormProps, connectForm } from 'form-container';
import * as React from 'react';
import { AEMError, Content, Errors, PoundOrPercent, SectionWrapperModel as FormModel, YesOrNo } from '../../interfaces';
import renderDataTest from '../../utilities/testAutomation';
import toggleAEMSibling from '../../utilities/toggleAEM';
import validators from '../../utilities/validators';
import ContentContext from '../App/Context';
import Button from '../Button/Button';
import PlansIcon from '../Icons/PlansIcon/PlansIcon';
import SwitchInput from '../Inputs/SwitchInput/SwitchInput';
import TextInput from '../Inputs/TextInput/TextInput';
import Outputs from '../Outputs/Outputs';
import PanelError from '../PanelError/PanelError';
import Tooltip from '../Tooltip/Tooltip';
import { ChevronPosition } from '../Icons/Chevron/Chevron';
import { calculate, CTCOutputModel } from '../../utilities/calculator';

import { Country } from '../../content/content';
import PersonIcon from '../Icons/PersonIcon/PersonIcon';

const Parser = require('html-react-parser');

interface IProps extends IFormProps<FormModel> {
  errors: AEMError;
}

interface IState {
  areInputsVisible: boolean;
  results: CTCOutputModel[] | string;
}

export interface InputModel {
  age: number;
  location?: Country;
  withdrawalAge: number;
  income?: number;
  scottishTaxPayer: string;
  totalValue: number;
  neededMoney: Number;
  employeeContribution?: Number;
  employeeContributionType?: string;
  employerContribution?: Number;
  employerContributionType?: string;
}

const {
  isRequired,
  isNumber,
  isInteger,
  isTenMillionOrLess,
  isPercentage,
  isFiftyFiveOrMore,
  isEighteenOrMore,
  isEightyOrLess,
  isOneHundredOrLess,
  isEqualOrMoreThanCurrentAge,
} = validators;

const selectError = (errorMessage: string, errorMessages: Errors) => errorMessages[errorMessage];

class SectionWrapper extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      areInputsVisible: true,
      results: [],
    };
  }

  handleSubmission = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();
    this.setState({ areInputsVisible: false });
    this.setState({ results: calculate(this.props.form.model) });
    if (this.props.form.model.stillPaying === YesOrNo.No) {
      const { model } = this.props.form;
      const updatedValues: InputModel = {
        age: model.age,
        scottishTaxPayer: model.scottishTaxPayer,
        totalValue: model.totalValue,
        withdrawalAge: model.withdrawalAge,
        neededMoney: model.neededMoney,
      };
      this.setState({ results: calculate(updatedValues) });
    }

    setTimeout(() => { this.outputSection.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
  }

  private inputSection: HTMLDivElement;
  private outputSection: HTMLDivElement;

  render() {
    const {
      form: {
        touched,
        validationErrors,
        model,
        model: { stillPaying },
        isValid,
      },
      formMethods: { bindInput },
      errors,
    } = this.props;
    const { areInputsVisible, results } = this.state;
    const isStillPaying = stillPaying === YesOrNo.Yes;


    toggleAEMSibling(false);
    return (
      <ContentContext.Consumer>
        {(content: Content) => {
          if (errors) {
            return (
              <PanelError
                title={errors.title}
                body={errors.body}
                button={<Button name={errors.button} link={errors.link} chevron={ChevronPosition.Left} />}
              />
            );
          }

          const {
            analytics,
            inputfields: {
              placeholder,
              errors: inputErrors,
            },
            inputs,
            inputs: {
              assumptions,
              age,
              plans,
              aboutYou,
              contributions,
              cta,
            },
          } = content;

          return (
            <div>
              {areInputsVisible &&
                <div className="input-wrapper" ref={(inputSection: HTMLDivElement) => { this.inputSection = inputSection; }}>
                  <div className="assumptions">{Parser(assumptions.text)}</div>
                  <div className="input-section input-section--top">
                    <div className="grid md--up header-wrapper">
                      <PersonIcon />
                      <h3>{Parser(aboutYou.title)}</h3>
                    </div>
                  </div>
                  <div className={classNames('grey-section')}>
                    <div className="input-section input-section--top">
                      <div className="grid md--up">
                        <div className="cell">
                          <div className="input-label-wrapper">
                            <p className="input-label">{Parser(age.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <TextInput
                                error={touched.age && validationErrors.age && selectError(validationErrors.age, inputErrors)}
                                placeholder={Parser(placeholder.enterAge)}
                                hideAddon
                                dataTest="ppc-input-age"
                                {...bindInput('age')}
                              />
                              <Tooltip
                                content={age.tooltip}
                                dataTest="ppc-input-age-tooltip"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="cell">
                          <div className="input-label-wrapper">
                            <p className="input-label">{Parser(inputs.scottishTaxPayer.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <SwitchInput
                                options={[YesOrNo.Yes, YesOrNo.No]}
                                dataTest="ppc-button-scottish-tax-payer"
                                data-dtm-id="toggle"
                                data-dtm-data={analytics.toggle}
                                {...bindInput('scottishTaxPayer')}
                              />
                              <Tooltip
                                content={inputs.scottishTaxPayer.tooltip}
                                dataTest="ppc-button-scottish-taxpayer-tooltip"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="cell">
                          <div className="input-label-wrapper">
                            <p className="input-label">{Parser(inputs.stillPaying.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <SwitchInput
                                options={[YesOrNo.Yes, YesOrNo.No]}
                                dataTest="ppc-button-still-paying"
                                data-dtm-id="toggle"
                                data-dtm-data={analytics.toggle}
                                {...bindInput('stillPaying')}
                              />
                              <Tooltip
                                content={inputs.stillPaying.tooltip}
                                dataTest="ppc-button-still-paying-tooltip"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {stillPaying && (
                    <div className="input-section">
                      <div className="input-section input-wrapper">
                        <div className="grid md--up header-wrapper">
                          <PlansIcon />
                          <h3>{Parser(plans.title)}</h3>
                        </div>
                      </div>
                      <div className="subsection">
                        <div className="grid md--up">
                          <div className="cell cell-1of3 input-label-wrapper">
                            <p className="input-label">{Parser(plans.totalValue.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <TextInput
                                error={touched.totalValue && validationErrors.totalValue && selectError(validationErrors.totalValue, inputErrors)}
                                placeholder={placeholder.enterAmount}
                                dataTest="ppc-plans-input-total-value"
                                {...bindInput('totalValue')}
                              />
                              <Tooltip
                                content={plans.totalValue.tooltip}
                                dataTest="ppc-plans-input-total-value-tooltip"
                              />
                            </div>
                          </div>
                          <div className="cell cell-1of3 input-label-wrapper">
                            <p className="input-label">{Parser(plans.withdrawalAge.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <TextInput
                                error={touched.withdrawalAge && validationErrors.withdrawalAge && selectError(validationErrors.withdrawalAge, inputErrors)}
                                disabled={model.age >= 80}
                                placeholder={(!model.age || model.age < 80) ? placeholder.enterAge : ' '}
                                hideAddon
                                dataTest="ppc-plans-input-withdrawal-age"
                                {...bindInput('withdrawalAge')}
                              />
                              <Tooltip
                                content={plans.withdrawalAge.tooltip}
                                dataTest="ppc-plans-input-withdrawal-age-tooltip"
                              />
                            </div>
                            {model.age >= 80 &&
                              <p
                                className="retirement-age-error"
                                {...renderDataTest('ppc-plans-input-withdrawal-age-disabled-error')}
                              >
                                {inputErrors.isAgeOver80}
                              </p>
                            }
                          </div>
                          <div className="cell cell-1of3 input-label-wrapper">
                            <p className="input-label">{Parser(plans.neededMoney.label)}</p>
                            <div className="input-tooltip-wrapper">
                              <TextInput
                                error={touched.neededMoney && validationErrors.neededMoney && selectError(validationErrors.neededMoney, inputErrors)}
                                placeholder={placeholder.enterAmount}
                                dataTest="ppc-plans-input-needed-money"
                                {...bindInput('neededMoney')}
                              />
                              <Tooltip
                                content={plans.neededMoney.tooltip}
                                dataTest="ppc-plans-input-needed-money-tooltip"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {isStillPaying &&
                        <>
                          <div className="input-section input-wrapper">
                            <div className="grid md--up header-wrapper">
                              <h3>{Parser(contributions.title)}</h3>
                            </div>
                          </div>
                          <div className="subsection">
                            <div className="grid md--up">
                              <div className="cell cell-1of3 input-label-wrapper">
                                <p className="input-label">{Parser(contributions.income.label)}</p>
                                <div className="input-tooltip-wrapper margin-top-auto">
                                  <TextInput
                                    error={touched.income && validationErrors.income && selectError(validationErrors.income, inputErrors)}
                                    placeholder={placeholder.enterAmount}
                                    dataTest="ppc-contributions-input-income"
                                    {...bindInput('income')}
                                  />
                                  <Tooltip
                                    content={contributions.income.tooltip}
                                    dataTest="ppc-contributions-input-income-tooltip"
                                  />
                                </div>
                              </div>
                              <div className="cell cell-1of3 input-label-wrapper">
                                <p className="input-label">{Parser(contributions.employeeContribution.label)}</p>
                                <SwitchInput
                                  options={[PoundOrPercent.Pound, PoundOrPercent.Percent]}
                                  dataTest="ppc-contributions-button-employee-contribution-type"
                                  {...bindInput('employeeContributionType')}
                                />
                                <div className="input-tooltip-wrapper">
                                  <TextInput
                                    dataTest="ppc-contributions-input-employee-contribution"
                                    error={touched.employeeContribution && validationErrors.employeeContribution && selectError(validationErrors.employeeContribution, inputErrors)}
                                    placeholder={placeholder.enterAmount}
                                    hideAddon
                                    {...bindInput('employeeContribution')}
                                  />
                                  <Tooltip
                                    content={contributions.employeeContribution.tooltip}
                                    dataTest="ppc-contributions-input-employee-contribution-tooltip"
                                  />
                                </div>
                              </div>
                              <div className="cell cell-1of3 input-label-wrapper">
                                <p className="input-label">{Parser(contributions.employerContribution.label)}</p>
                                <SwitchInput
                                  options={[PoundOrPercent.Pound, PoundOrPercent.Percent]}
                                  dataTest="ppc-contributions-button-employer-contribution-type"
                                  {...bindInput('employerContributionType')}
                                />
                                <div className="input-tooltip-wrapper">
                                  <TextInput
                                    error={touched.employerContribution && validationErrors.employerContribution && selectError(validationErrors.employerContribution, inputErrors)}
                                    placeholder={!model.employerContributionType ? placeholder.enterAmountOptional : placeholder.enterAmount}
                                    hideAddon
                                    dataTest="ppc-contributions-input-employer-contribution"
                                    {...bindInput('employerContribution')}
                                  />
                                  <Tooltip
                                    content={contributions.employerContribution.tooltip}
                                    dataTest="ppc-contributions-input-employer-contribution-tooltip"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      }
                      <Button
                        name={cta.label}
                        onClick={this.handleSubmission}
                        isDisabled={!isValid}
                        data-dtm-id="buttons"
                        data-dtm-data={analytics.button.calculate}
                        {...renderDataTest('ppc-button-reveal-options')}
                      />
                    </div>
                  )}
                </div>
              }
              {!areInputsVisible &&
                <div ref={(output: HTMLDivElement) => { this.outputSection = output; }}>
                  <Outputs
                    inputModel={model}
                    hideOutputs={() => {
                      this.setState({ areInputsVisible: true });
                      setTimeout(() => {
                        this.inputSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 300);
                    }}
                    results={results}
                  />
                </div>
              }
            </div>
          );
        }}
      </ContentContext.Consumer>
    );
  }
}

const isStillPaying = (model: FormModel) => model.stillPaying === YesOrNo.Yes;
const isAgeOverEighty = (model: FormModel) => model.age < 80;
const formValidators = [
  isTenMillionOrLess('employerContribution', (model) => model.employerContributionType === PoundOrPercent.Pound),
  isPercentage('employerContribution', (model) => model.employerContributionType === PoundOrPercent.Percent),
  isNumber('employerContribution', (model) => model.stillPaying === YesOrNo.Yes && !!model.employerContribution),
  isRequired('employerContribution',
    (model) => model.stillPaying === YesOrNo.Yes && !!model.employerContributionType),
  isRequired('employerContributionType',
    (model) => model.stillPaying === YesOrNo.Yes && !!model.employerContribution),
  isTenMillionOrLess('employeeContribution', (model) => model.employeeContributionType === PoundOrPercent.Pound),
  isPercentage('employeeContribution', (model) => model.employeeContributionType === PoundOrPercent.Percent),
  isNumber('employeeContribution', isStillPaying),
  isRequired('employeeContribution', isStillPaying),
  isRequired('employeeContributionType', isStillPaying),
  isTenMillionOrLess('income', isStillPaying),
  isNumber('income', isStillPaying),
  isRequired('income', isStillPaying),
  isInteger('age'),
  isEighteenOrMore('age'),
  isOneHundredOrLess('age'),
  isInteger('withdrawalAge', isAgeOverEighty),
  isFiftyFiveOrMore('withdrawalAge', isAgeOverEighty),
  isEightyOrLess('withdrawalAge', isAgeOverEighty),
  isEqualOrMoreThanCurrentAge('withdrawalAge', isAgeOverEighty),
  isTenMillionOrLess('neededMoney'),
  isTenMillionOrLess('totalValue'),
  isNumber('neededMoney'),
  isNumber('withdrawalAge', isAgeOverEighty),
  isNumber('totalValue'),
  isNumber('age'),
  isRequired('neededMoney'),
  isRequired('withdrawalAge', isAgeOverEighty),
  isRequired('totalValue'),
  isRequired('stillPaying'),
  isRequired('age'),
  isRequired('scottishTaxPayer'),
];

export default connectForm(formValidators)(SectionWrapper);



// WEBPACK FOOTER //
// ./src/components/SectionWrapper/SectionWrapper.tsx